import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { ChangeAccessLevelConfirmDialog } from "@components/project/change-access-level-confirm-dialog";
import { AccessLevelDropdown } from "@components/access-level/access-level-dropdown";
import { AccessLevelTooltip } from "@components/access-level/access-level-tooltip";
import { useOnAccessLevelChange } from "@hooks/projects/use-on-access-level-change";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Stack } from "@mui-wrapper";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { DEFAULT_INPUT_FONT_SIZE } from "@styles/common-styles";
import { useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";

/** Component width */
const ACCESS_LEVEL_COMPONENT_WIDTH = "225px";

/**
 * Access level selection component
 * If the user has permission to change the access level it renders a custom dropdown,
 * otherwise it shows the access level as readonly text.
 */
export function AccessLevel({
  type,
  isAccessLevelEditable,
  entity,
  location,
  sx
}) {
  const {
    isScreenXs
  } = useMediaQueryList();
  const {
    onAccessLevelChange
  } = useOnAccessLevelChange(type);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  function handleSelectAccessLevel(accessLevel) {
    setSelectedAccessLevel(accessLevel);
    setIsDialogOpen(true);
  }
  async function handleConfirm() {
    if (!selectedAccessLevel) {
      return;
    }
    setIsLoading(true);
    try {
      await onAccessLevelChange(selectedAccessLevel, location, entity.id);
    } finally {
      setIsLoading(false);
      setIsDialogOpen(false);
    }
  }
  return <Stack sx={{
    width: "100%",
    ...sx
  }}>
      <AccessLevelTooltip type={type} />
      {isAccessLevelEditable ? <>
          <FaroTextMenuButton buttonTitle={FaroCoreApiUtils.Project.AccessLevelDisplayNames[entity.accessLevel]} buttonSx={() => {
        return {
          color: "text.primary",
          fontSize: DEFAULT_INPUT_FONT_SIZE,
          // For extra-small screens show the component in full width
          width: isScreenXs ? "100%" : ACCESS_LEVEL_COMPONENT_WIDTH,
          textOverflow: "ellipsis",
          justifyContent: "space-between",
          // Not focused
          "&:not(&:focus)": {
            // Hide the underline and icon by default
            "&:before": {
              border: "none"
            },
            "& .MuiButton-icon": {
              display: "none"
            },
            // Hover style. Show underline and icon
            "&:hover": {
              boxShadow: `0px 1px 0px ${PALETTE_VARS.text.disabled}`,
              "& .MuiButton-icon": {
                display: "block"
              }
            }
          },
          // Focused
          "&:focus": {
            borderBottom: "2px solid",
            borderBottomColor: PALETTE_VARS.primary.main
          }
        };
      }}>
            <AccessLevelDropdown type={type} currentAccessLevel={entity.accessLevel} onSelectAccessLevel={handleSelectAccessLevel} />
          </FaroTextMenuButton>

          {selectedAccessLevel && <ChangeAccessLevelConfirmDialog type={type} accessLevel={selectedAccessLevel} isOpen={isDialogOpen} isLoading={isLoading} onConfirm={() => void handleConfirm()} onClose={() => setIsDialogOpen(false)} />}
        </> : <FaroTextField initialValue={FaroCoreApiUtils.Project.AccessLevelDisplayNames[entity.accessLevel]} sx={{
      width: ACCESS_LEVEL_COMPONENT_WIDTH
    }} />}
    </Stack>;
}